import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";
import { Check, Sparkles, Shield, Zap, Building2, Users, Crown, Gift } from "lucide-react";

const PLANS = [
  {
    id: "trial",
    name: "Trial 14 giorni",
    icon: Gift,
    price: "€1",
    priceSubtitle: "una tantum (validazione carta)",
    description: "Prova gratis tutte le funzionalità per 14 giorni. Addebitiamo €1 per validare la carta — verrà restituito.",
    features: ["Tutte le funzionalità", "Fino a 5 dipendenti", "Validazione carta €1", "Cancella in qualsiasi momento"],
    cta: "Inizia trial · €1",
    highlight: false,
    target_plan_id: "tier10_monthly",
  },
  {
    id: "tier10_monthly",
    name: "Tier 10",
    icon: Zap,
    price: "€20,00",
    priceSubtitle: "/mese · setup €199 · €1/dip.",
    description: "Per piccole realtà fino a 10 dipendenti.",
    features: ["Fino a 10 dipendenti", "Calendario + QR geolocalizzato", "Ferie e cedolini", "Export CSV paghe", "Supporto email"],
    cta: "Scegli Tier 10",
    highlight: false,
  },
  {
    id: "tier20_monthly",
    name: "Tier 20",
    icon: Building2,
    price: "€40,00",
    priceSubtitle: "/mese · setup €199 · €1/dip.",
    description: "Per team in crescita fino a 20 dipendenti.",
    features: ["Fino a 20 dipendenti", "Tutto Tier 10", "Rimborsi spese", "Firma digitale base", "Multi-utente admin"],
    cta: "Scegli Tier 20",
    highlight: false,
  },
  {
    id: "tier30_monthly",
    name: "Tier 30",
    icon: Crown,
    price: "€60,00",
    priceSubtitle: "/mese · setup €299 · €1/dip.",
    description: "Il più scelto dalle PMI italiane.",
    features: ["Fino a 30 dipendenti", "Tutto Tier 20", "AI analisi presenze", "Trasferte GPS", "Turni & Timesheet"],
    cta: "Scegli Tier 30",
    highlight: true,
  },
  {
    id: "tier50_monthly",
    name: "Tier 50",
    icon: Crown,
    price: "€100,00",
    priceSubtitle: "/mese · setup €399 · €1/dip.",
    description: "Per aziende strutturate fino a 50 dipendenti.",
    features: ["Fino a 50 dipendenti", "Tutto Tier 30", "Multi-sede illimitate", "Onboarding dedicato", "Customer Success"],
    cta: "Scegli Tier 50",
    highlight: false,
  },
];

export default function OnboardingPlan() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);

  const choose = async (plan) => {
    setLoading(plan.id);
    try {
      const origin = window.location.origin;
      let r;
      if (plan.id === "trial") {
        r = await api.post("/payments/trial-checkout", { origin_url: origin, target_plan_id: plan.target_plan_id || "tier10_monthly" });
      } else {
        r = await api.post("/payments/checkout", { plan_id: plan.id, origin_url: origin });
      }
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio pagamento");
      setLoading(null);
    }
  };

  const skip = () => nav("/app");

  return (
    <div className="min-h-screen bg-[#0A0E27] px-4 sm:px-8 py-10 relative overflow-hidden">
      <div className="orb orb-orange" style={{width: 600, height: 600, top: -300, right: -200, opacity: 0.18}}/>
      <div className="orb orb-orange" style={{width: 400, height: 400, bottom: -100, left: -100, opacity: 0.14}}/>
      <div className="relative max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <LogoMark size={56} pulse/>
          <button onClick={skip} className="text-xs text-[#757575] hover:text-[#3B82F6]" data-testid="onboarding-skip">Salta per ora →</button>
        </div>
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E2347] text-[#3B82F6] text-[10px] uppercase tracking-widest font-bold mb-4">
            <Sparkles className="w-3 h-3"/> Step finale · Scegli il tuo piano
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mb-3">Quasi fatto. Scegli il piano giusto per te.</h1>
          <p className="text-[#555] text-base">Cambia o annulla in qualsiasi momento. Tutti i piani includono SSL, backup giornalieri e supporto in italiano.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.id} className={`card-smart p-6 flex flex-col relative ${p.highlight ? "border-2 border-[#3B82F6] shadow-lg shadow-[#3B82F6]/30" : ""}`} data-testid={`plan-card-${p.id}`}>
                {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#3B82F6] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full">Più scelto</div>}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${p.id === "trial" ? "bg-green-100 text-green-700" : "bg-[#1E2347] text-[#3B82F6]"}`}>
                  <Icon className="w-6 h-6"/>
                </div>
                <h3 className="font-display text-2xl">{p.name}</h3>
                <div className="mt-2">
                  <div className="font-display text-4xl text-[#0A0A0A]">{p.price}</div>
                  <div className="text-xs text-[#757575] mt-0.5">{p.priceSubtitle}</div>
                </div>
                <p className="text-sm text-[#555] mt-3 leading-relaxed flex-1">{p.description}</p>
                <ul className="space-y-2 mt-5 mb-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${p.id === "trial" ? "text-green-600" : "text-[#3B82F6]"}`}/>
                      <span className="text-[#444]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => choose(p)}
                  disabled={loading !== null}
                  data-testid={`plan-cta-${p.id}`}
                  className={`w-full rounded-full h-11 ${p.highlight || p.id === "trial" ? "bg-[#3B82F6] hover:bg-[#7C3AED] text-white btn-glow" : "bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white"}`}
                >
                  {loading === p.id ? "Reindirizzamento…" : p.cta}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-xs text-[#757575] flex items-center justify-center gap-2 flex-wrap">
          <Shield className="w-4 h-4 text-[#3B82F6]"/>
          Pagamenti sicuri tramite Stripe · Carte accettate: Visa, Mastercard, Amex, Apple Pay, Google Pay · GDPR & PSD2 compliant
        </div>
        <div className="mt-3 text-center text-[10px] text-[#999] max-w-3xl mx-auto leading-relaxed">
          Per il Trial 14 giorni: addebitiamo €1 per validare la carta. L'importo verrà rimborsato automaticamente entro 7 giorni lavorativi se annulli prima della fine del periodo di prova.
        </div>
      </div>
    </div>
  );
}
